// What the web needs to know about each screen that has adopted saved views:
// the URL keys of its filters, the parameter its order rides on with its
// declared default (spec 16 §3.12 items 1 and 2, D-305 rulings 2, 3, 5 and 17),
// and, since P3-E2 PR E2-1, the lineages the view gate admits where the
// screen names them (spec 16 §7, D-314 ruling 6). Six screens: PR-E4's four,
// and the deal list and the task list from E2-1.
//
// The authority is the contracts registry, `SAVED_VIEW_SCREENS` in
// packages/contracts/src/saved-views/screens.ts. It is restated here rather
// than imported, because the contracts root pulls zod and the OpenAPI
// registry into the office bundle (D-096 ruling 4, the reason
// `components/data-table.tsx` restates the column limits), and the registry
// has no zero-import subpath. `views-contract.test.ts` holds this table equal to the
// registry in both directions, key by key, so a filter the contract adds or
// drops fails there rather than widening a list in silence.

import type { SavedViewScreenKey } from "./view-state";

export interface SavedViewScreenWeb {
  /** The screen's URL filter keys, the keys a view's `config.filters` holds. */
  readonly filterKeys: readonly string[];
  /**
   * The lineages the server's saved-view gate admits on this screen, where
   * its list route narrows by lineage (spec 16 §7, D-314 ruling 6), restated
   * so `views-contract.test.ts` can hold the screen's web audience to it: a
   * role the web offers the list to is a role the gate admits, and a role the
   * gate refuses is never offered the list. Absent where the read code is the
   * whole gate. The client never judges a lineage itself, since the role feed
   * carries literal codes and a tenant's clone would read as a stranger; a
   * reader the gate refuses anyway (a composed role) meets the views read's
   * 403, which hides the switcher and leaves the list working (V3).
   */
  readonly readLineages?: readonly string[];
  readonly order: {
    /** The URL key the sort token rides on (`sort` on the work-order list). */
    readonly param: "order" | "sort";
    /** The token the list route applies when the parameter is absent. */
    readonly declaredDefault: string;
  };
}

/**
 * The longest text a search box on an adopting screen may hold, the
 * contract's `SearchText` max (1 to 200 characters, no NUL; the `q` filter of
 * `production.work_orders`, `sales.customers` and `sales.opportunities`). One constant for every such
 * box's `maxLength` and every such screen's URL validator, so the box, the
 * address, the list read and a saved view hold one cap (R7, PR-E4 wave 4).
 * The contract keeps the number private, so `views-contract.test.ts` probes
 * each screen's `q` schema at this length and one past it.
 */
export const VIEW_SEARCH_TEXT_MAX = 200;

/** A uuid, loosely: the shape the platform mints (v7) without pinning the
 * version, which is what the contract's `z.guid()` accepts. */
const VIEW_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;

/**
 * The address's `view` as every adopting screen's validator reads it (W8,
 * PR-E4 wave 5): a view id, which is a uuid, or absent. A malformed value
 * cannot name any view, so it reads as absent, with no notice, the way every
 * other malformed parameter reads, and the list opens on the rest of the
 * address. "This view no longer exists" is kept for an id the views read
 * does not hold (spec 16 §3.12 item 9). One rule for every adopting screen,
 * so a pasted `?view=overdue` behaves the same on the sales books, the deal
 * and task lists and the work-order list.
 */
export function viewIdParam(value: unknown): string | undefined {
  return typeof value === "string" && VIEW_ID_RE.test(value) ? value : undefined;
}

export const SAVED_VIEW_SCREENS_WEB = {
  "production.work_orders": {
    filterKeys: [
      "status",
      "facility_id",
      "work_center_id",
      "product_id",
      "due_before",
      "due_after",
      "overdue",
      "q",
    ],
    // Spec 04 §5 row 2: an absent `sort` is `created_at` ascending.
    order: { param: "sort", declaredDefault: "created_at" },
  },
  "sales.customers": {
    // `needs_next_step` joined in E2-1's web wave (D-314 ruling 9), and the
    // tag filter `tag` (the wire's `tag_id`) in E2-2a1's web wave (TF1, D-323
    // ruling 6).
    filterKeys: ["q", "tier", "status", "owner", "credit_hold", "needs_next_step", "tag"],
    order: { param: "order", declaredDefault: "-created_at" },
    // P3-E2 PR E2-1 (D-314 ruling 6): the book's route refuses qc and
    // storekeeper, and since E2-1 so does the view gate.
    readLineages: ["group_admin", "facility_admin", "sales", "finance", "planner"],
  },
  "sales.quotes": {
    filterKeys: ["customer", "status", "origin", "owner", "off_list", "created_from", "created_to"],
    order: { param: "order", declaredDefault: "-created_at" },
    // D-337 ruling 7: the quote routes refuse qc and storekeeper by lineage,
    // and so does the view gate.
    readLineages: ["group_admin", "facility_admin", "sales", "finance", "planner"],
  },
  "sales.orders": {
    filterKeys: ["customer", "status", "facility"],
    order: { param: "order", declaredDefault: "-confirmed_at" },
  },
  // P3-E2 PR E2-1 (spec 09 §4.14, D-314 ruling 6): the deal list. A view
  // stores `status` only as won or lost and the two flags only as true; `peek`
  // is screen state and never a filter (ruling 8).
  "sales.opportunities": {
    filterKeys: [
      "status",
      "stage",
      "owner",
      "customer",
      "close_from",
      "close_to",
      "rotting",
      "needs_next_step",
      "lost_reason",
      "q",
    ],
    // Ties break by `created_at` and then `id`, the board's order.
    order: { param: "order", declaredDefault: "expected_close_month" },
    readLineages: ["group_admin", "facility_admin", "sales", "finance", "planner"],
  },
  // P3-E2 PR E2-1 (spec 09 §4.12, D-313 ruling 4): the task list. No kind
  // filter; `assignee` is offered to a literal admin only; no lineage, since
  // narrowing 10 narrows the rows and refuses no lineage.
  "sales.tasks": {
    filterKeys: ["scope", "status", "due", "customer", "assignee"],
    // PR-E2's status-dependent order, as one token.
    order: { param: "order", declaredDefault: "worklist" },
  },
} as const satisfies Record<SavedViewScreenKey, SavedViewScreenWeb>;
